import { scrollLock } from '@theme/events'
import {
  supportsIntersectionObserver,
  useIntersection
} from '@theme/use-intersection'

const ChatButtonConfig = {
  selectors: {
    hoverAction: '[data-modal-whatsapp-action="hover"]',
    toggleAction: '[data-modal-whatsapp-action="toggle"]'
  },
  attributes: {
    status: 'data-modal-whatsapp-status'
  }
}

class ChatButton extends HTMLElement {
  constructor() {
    super()
    this.state = {
      isScrolled: false,
      isFooterVisible: false
    }
    this.ticking = false
    this.unobserveFooter = null
    this.canHover = false

    this.handleScroll = this.handleScroll.bind(this)
    this.handleIntersect = this.handleIntersect.bind(this)
    this.updateVisibility = this.updateVisibility.bind(this)
    this.handleHoverEnter = this.handleHoverEnter.bind(this)
    this.handleHoverLeave = this.handleHoverLeave.bind(this)
    this.handleToggle = this.handleToggle.bind(this)
    this.handleKeydown = this.handleKeydown.bind(this)
  }

  connectedCallback() {
    this.wrapper = this.closest('aside') || this
    this.footer = document.querySelector('footer')

    this.hoverWrap = this.querySelector(ChatButtonConfig.selectors.hoverAction)
    this.toggleBtn = this.querySelector(ChatButtonConfig.selectors.toggleAction)

    this.canHover = window.matchMedia
      ? window.matchMedia('(hover: hover) and (pointer: fine)').matches
      : true

    this.state.isScrolled = window.scrollY > 100
    this.updateVisibility()

    if (this.footer) {
      this.unobserveFooter?.()
      const footerObserver = useIntersection({
        rootMargin: '0px 0px 100px 0px'
      })
      this.unobserveFooter = footerObserver.observe(
        this.footer,
        this.handleIntersect
      )
    }

    window.addEventListener('scroll', this.handleScroll, {
      passive: true
    })
    document.addEventListener('keydown', this.handleKeydown)

    if (this.hoverWrap) {
      if (!this.hasAttribute(ChatButtonConfig.attributes.status)) {
        this.setAttribute(ChatButtonConfig.attributes.status, 'not-active')
      }
      this.hoverWrap.addEventListener('mouseenter', this.handleHoverEnter)
      this.hoverWrap.addEventListener('mouseleave', this.handleHoverLeave)
      this.hoverWrap.addEventListener('focusin', this.handleHoverEnter)
      this.hoverWrap.addEventListener('focusout', this.handleHoverLeave)
    }

    if (this.toggleBtn) {
      this.toggleBtn.addEventListener('click', this.handleToggle)
    }
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.handleScroll)
    document.removeEventListener('keydown', this.handleKeydown)

    this.unobserveFooter?.()
    this.unobserveFooter = null

    if (this.hoverWrap) {
      this.hoverWrap.removeEventListener('mouseenter', this.handleHoverEnter)
      this.hoverWrap.removeEventListener('mouseleave', this.handleHoverLeave)
      this.hoverWrap.removeEventListener('focusin', this.handleHoverEnter)
      this.hoverWrap.removeEventListener('focusout', this.handleHoverLeave)
    }

    if (this.toggleBtn) {
      this.toggleBtn.removeEventListener('click', this.handleToggle)
    }
  }

  handleIntersect(entry) {
    if (!entry) return
    const isIntersecting = supportsIntersectionObserver()
      ? entry.isIntersecting
      : false
    this.state.isFooterVisible = isIntersecting
    this.updateVisibility()
  }

  handleScroll() {
    if (this.ticking) {
      return
    }
    window.requestAnimationFrame(() => {
      const scrolled = window.scrollY > 100
      if (this.state.isScrolled !== scrolled) {
        this.state.isScrolled = scrolled
        this.updateVisibility()
      }
      this.ticking = false
    })
    this.ticking = true
  }

  updateVisibility() {
    const shouldShow = this.state.isScrolled && !this.state.isFooterVisible
    this.wrapper.classList.toggle('is-visible', shouldShow)
    this.wrapper.classList.toggle('is-hidden', !shouldShow)
    this.wrapper.classList.toggle('pointer-events-auto', shouldShow)
    this.wrapper.classList.toggle('pointer-events-none', !shouldShow)
    this.wrapper.setAttribute('aria-hidden', shouldShow ? 'false' : 'true')
  }

  setModalStatus(status) {
    this.setAttribute(ChatButtonConfig.attributes.status, status)

    const isActive = status === 'active'
    scrollLock(isActive)
  }

  handleHoverEnter(event) {
    if (event?.type?.startsWith('mouse') && !this.canHover) return

    if (
      this.getAttribute(ChatButtonConfig.attributes.status) === 'not-active'
    ) {
      this.setModalStatus('active')
    }
  }

  handleHoverLeave(event) {
    if (event?.type?.startsWith('mouse') && !this.canHover) return
    if (event?.relatedTarget && this.hoverWrap?.contains(event.relatedTarget))
      return

    if (this.getAttribute(ChatButtonConfig.attributes.status) === 'active') {
      this.setModalStatus('not-active')
    }
  }

  handleToggle() {
    if (this.canHover) return

    const currentStatus = this.getAttribute(ChatButtonConfig.attributes.status)
    const newStatus = currentStatus === 'not-active' ? 'active' : 'not-active'
    this.setModalStatus(newStatus)
  }

  handleKeydown(event) {
    if (event.keyCode === 27) {
      this.setModalStatus('not-active')
    }
  }
}

if (!customElements.get('chat-button')) {
  customElements.define('chat-button', ChatButton)
}
